import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, PresentationIcon, Share2Icon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CAMPAIGN_CHANNEL_IMAGE = 'https://cdn.magicpatterns.com/uploads/wP8uTkCzBviEz4xMWz9kYK/Screenshot_2026-09-09_at_10.10.47.png';
interface Channel {
  label: string;
  detail: string;
  logo?: string;
  logos?: readonly string[];
  icon?: typeof PresentationIcon;
}
const CHANNELS: readonly Channel[] = [{
  label: 'Embed',
  detail: 'Websites and portals',
  logo: 'https://cdn.magicpatterns.com/uploads/p8hxGk8PzPNEJJ1JuXxQRJ/image.png'
}, {
  label: 'SMS & WhatsApp',
  detail: 'Direct links, conversation-ready',
  logos: ['https://cdn.magicpatterns.com/uploads/2mqR9WX6acGmcEwvCVyefL/image.png', 'https://cdn.magicpatterns.com/uploads/ff2xrTo8njegc9HQfBiHHs/image.jpg']
}, {
  label: 'Sales Rep detailer (iCVA)',
  detail: 'On/offline FtF presentations',
  icon: PresentationIcon
}, {
  label: 'QR Code',
  detail: 'Print and events',
  logo: 'https://cdn.magicpatterns.com/uploads/5EBPJkAW8wtsVjG6Pdmtzv/image.png'
}, {
  label: 'Post',
  detail: 'Organic and paid social',
  logo: 'https://cdn.magicpatterns.com/uploads/i3HXhvnPCcq7MnMVtgzoGz/image.png'
}, {
  label: 'Email',
  detail: 'CRM and newsletters',
  logo: 'https://cdn.magicpatterns.com/uploads/eaVbSy8gkaLMxVXZn426xD/image.png'
}, {
  label: 'Advertise',
  detail: 'Banners and paid media',
  logo: 'https://cdn.magicpatterns.com/uploads/x8cuoAZY8NxEd27vHPMXd5/image.png'
}, {
  label: 'Congress',
  detail: 'Booths and sessions',
  logo: 'https://cdn.magicpatterns.com/uploads/gFpiinUzyP3uSTeNYjp6JX/image.png'
}];
export function Slide03Background({}: SlideProps) {
  return <InteriorSlide surface data-id="element-78" data-mp="ja9uev1vbdm0">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.25rem,2vw,2rem)] flex-shrink-0" data-id="element-79" data-mp="ja02v316cye7">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-80" data-mp="2ig5gohk8n2vm">
          Channel-agnostic distribution
        </p>
        <h2 className="max-w-5xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-81" data-mp="28r41uupfirj8">
          One experience.
          <span className="text-[var(--brand)]" data-id="element-82" data-mp="1uqb4813tzgbw"> More channels to share.</span>
        </h2>
        <p className="mt-3 max-w-3xl text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-83" data-mp="1zsa594c3m15o">
          A single Snak can meet HCPs wherever attention already exists—without
          rebuilding the experience for every channel.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-7 lg:grid-cols-12 lg:gap-10" data-id="element-84" data-mp="3f8ipr0du85t5">
        <motion.article initial={{
        opacity: 0,
        x: -24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.18,
        duration: 0.6
      }} className="relative flex min-h-0 flex-col overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.25rem,2vw,2rem)] text-white shadow-lg lg:col-span-5" data-id="element-85" data-mp="3k6slpd7dzaft">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-86" data-mp="1iabh3r6zsy4l" />
          <div className="relative flex items-center gap-3" data-id="element-87" data-mp="1dg78z4lzqk1x">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-88" data-mp="1r8nbm4azcg2b">
              <MessageCircleIcon className="h-6 w-6" aria-hidden="true" data-id="element-89" data-mp="1n71x6eisrtq0" />
            </span>
            <div data-id="element-90" data-mp="216tf2qcfpu9a">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-white/65" data-id="element-91" data-mp="217clpawmww80">
                Example entry point
              </p>
              <h3 className="text-2xl font-extrabold" data-id="element-92" data-mp="6ttvyn8uihx9">WhatsApp</h3>
            </div>
          </div>

          <div className="relative mx-auto mt-5 min-h-0 w-full max-w-sm flex-1 overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-white/25" data-id="element-93" data-mp="1uqpqzh4q5np">
            <img src={CAMPAIGN_CHANNEL_IMAGE} alt="Interactive brand quiz shared with an HCP through WhatsApp" className="h-full w-full object-contain" data-id="element-94" data-mp="3be6zdekpfwc" />
          </div>
        </motion.article>

        <motion.section initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-7" aria-labelledby="channel-heading" data-id="element-95" data-mp="i8wukcfiln27">
          <div className="flex items-end justify-between gap-4" data-id="element-96" data-mp="3lnsvmmg80ilg">
            <div data-id="element-97" data-mp="3qo86n7d15e09">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-98" data-mp="3qnoygid6m3zf">
                The same Snak
              </p>
              <h3 id="channel-heading" className="mt-1 text-2xl font-extrabold text-[var(--fg)]" data-id="element-99" data-mp="2y0kmtujl2reo">
                Ready for every relevant touchpoint
              </h3>
            </div>
            <span className="hidden rounded-full bg-[var(--brand)]/10 px-4 py-2 text-xs font-bold text-[var(--brand)] sm:inline-flex" data-id="element-100" data-mp="1k4ayfwkf20l5">
              One build · many entries
            </span>
          </div>

          <div className="mt-5 grid min-h-0 flex-1 grid-cols-2 gap-3 md:grid-cols-4" data-id="element-101" data-mp="1b3cv7ghsxx3m">
            {CHANNELS.map((channel, index) => <motion.article key={channel.label} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.42 + index * 0.06,
            duration: 0.4
          }} className="group flex min-h-0 flex-col justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm transition-transform hover:-translate-y-1" data-id="element-102" data-mp="163qfn0ei44rn" data-mpi={index}>
                {channel.logos ? <span className="flex items-center gap-1.5" data-id="element-103" data-mp="ienzxy3fglzs">
                    {channel.logos.map(logo => <span key={logo} className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface)] p-1.5" data-id="element-104" data-mp="dfku4bfmibqr">
                        <img src={logo} alt="" className="h-full w-full object-contain" data-id="element-105" data-mp="8rlf8a4njx4b" />
                      </span>)}
                  </span> : channel.icon ? <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--brand)]/10" data-id="element-106" data-mp="mjrl93e3cnq1">
                    <channel.icon className="h-6 w-6 text-[var(--brand)]" aria-hidden="true" data-id="element-107" data-mp="rgi1prtt5lbz" />
                  </span> : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface)] p-1.5" data-id="element-108" data-mp="hqgmzqnchf9q">
                    <img src={channel.logo} alt={`${channel.label} channel logo`} className="h-full w-full object-contain" data-id="element-109" data-mp="15fhoetxrfq5i" />
                  </span>}
                <div className="mt-4" data-id="element-110" data-mp="2q0eqfiblji4q">
                  <h4 className="text-sm font-extrabold leading-tight text-[var(--fg)]" data-id="element-111" data-mp="2pzd3afhb356n">
                    {channel.label}
                  </h4>
                  <p className="mt-1 text-[10px] leading-snug text-[var(--muted)]" data-id="element-112" data-mp="2zowmjm8xl121">{channel.detail}</p>
                </div>
              </motion.article>)}
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-5 py-3" data-id="element-113" data-mp="270sgv0qd0nzc">
            <Share2Icon className="h-5 w-5 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-114" data-mp="2lz2wj0lowaqq" />
            <p className="text-sm font-semibold text-[var(--fg)]" data-id="element-115" data-mp="2gxx310k7gjg5">
              Consistent experience, consistent measurement—regardless of where the journey begins.
            </p>
          </div>
        </motion.section>
      </div>
    </InteriorSlide>;
}